import React from 'react';
import cx from 'classnames';
import styles from './horizontalProgress.module.scss';
import { MAX_PERFORMANCE } from '@cp/src/constants/Global';
import PerformanceMeterText from '@cp/src/assets/images/icons/PerformanceMeterText';
import { toFixedValueNotation } from '@cp/src/utils/commonUtils';
import { Tooltip, TooltipTrigger, TooltipContent } from '@cp/src/components/TooltipV2/TooltipV2';

interface IHorizontalProgress {
  percentage?: number;
  className: string;
  percentageClassName?: string;
  multiProgressCounts?: number[];
  multiProgressColors?: string[];
  toShowPerformanceMeter?: boolean;
  showTooltip?: boolean;
  tooltipContent?: React.ReactNode;
}

const HorizontalProgress: React.FC<IHorizontalProgress> = props => {
  const {
    percentage = 0,
    className,
    percentageClassName,
    multiProgressColors,
    multiProgressCounts,
    toShowPerformanceMeter = false,
    showTooltip = false,
    tooltipContent = ''
  } = props;

  if (multiProgressCounts) {
    const total = multiProgressCounts.reduce((sum, count) => sum + count, 0);
    return (
      <div className={cx(styles.progress, className)}>
        {multiProgressCounts.map((count, index) => {
          const width = total > 0 ? `${(count / total) * 100}%` : '0%';
          return (
            <div
              key={index}
              className={cx(styles.progressSegment, 'h-full')}
              style={{ width, backgroundColor: multiProgressColors?.[index] }}
            />
          );
        })}
      </div>
    );
  }

  const newStyle = {
    width: percentage >= MAX_PERFORMANCE ? `95%` : `${percentage}%`
  };

  return (
    <div className={cx([styles.progress, className])}>
      <div className={cx(styles.progressDone, percentageClassName)} style={newStyle} />
      {toShowPerformanceMeter && percentage >= MAX_PERFORMANCE ? (
        showTooltip ? (
          <div className="absolute bottom-[-36px] right-[-15px]">
            <div className="relative">
              <Tooltip>
                <TooltipTrigger>
                  <div className="relative">
                    <div className="absolute inset-0 z-[var(--z-index-map-options)]" />
                    <PerformanceMeterText />
                    <div className={styles.performanceText}>
                      {toFixedValueNotation(String(percentage), 0)}
                    </div>
                  </div>
                </TooltipTrigger>
                <TooltipContent className={cx('tooltipWrapper', 'absolute')}>
                  {tooltipContent}
                </TooltipContent>
              </Tooltip>
            </div>
          </div>
        ) : (
          <div className="absolute bottom-[-36px] right-[-36px]">
            <div>
              <PerformanceMeterText />
              <div className={styles.performanceText}>
                <div>{toFixedValueNotation(String(percentage), 0)}</div>
              </div>
            </div>
          </div>
        )
      ) : null}

      {showTooltip && percentage > 0 && (
        <Tooltip>
          <TooltipTrigger asChild>
            <div
              className="absolute w-4 h-4 rounded-full"
              style={{
                left: `calc(${newStyle.width} - 8px)`,
                top: '50%',
                transform: 'translateY(-50%)'
              }}
            />
          </TooltipTrigger>
          <TooltipContent className="tooltipWrapper">{tooltipContent}</TooltipContent>
        </Tooltip>
      )}
    </div>
  );
};

export default HorizontalProgress;
